import type { TrendingTopic } from '@benzinga/trending-topics-manager';
import type { Session, Ticker } from '@benzinga/session';
import { QuotesManager } from '@benzinga/quotes-manager';
import { CryptoManager, getCryptoLogo, getCryptoLogoMap, isUSDCryptoSymbol } from '@benzinga/crypto-manager';

export const enrichTrendingTickerLogos = async (tickers: TrendingTopic[], session: Session): Promise<Ticker[]> => {
  const tickerSymbols = tickers.map(t => t.name ?? '');
  if (tickerSymbols.length === 0) return [];

  const quotesManager = session.getManager(QuotesManager);
  const quotesRes = await quotesManager.getDelayedQuotes(tickerSymbols).catch(() => ({ ok: {} }));
  const quotesMap = quotesRes?.ok ?? {};

  const logos = await quotesManager.getQuotesLogosMapped(tickerSymbols, {
    fields: 'mark_composite_light,mark_composite_dark',
    search_keys_type: 'symbol',
  });
  const logoMap = logos?.ok ?? {};
  let enriched: Ticker[] = tickers.map(ticker => {
    if (ticker.taxonomy !== 'tickers') return ticker;
    const quote = quotesMap[ticker.name ?? ''] ?? {};
    const file = logoMap[ticker.name ?? '']?.files;
    const logo = file?.mark_composite_light ?? file?.mark_composite_dark ?? null;
    return { ...ticker, ...quote, logo };
  });

  const needsCrypto = enriched.filter(t => !t.logo && isUSDCryptoSymbol(t.symbol || t.name));
  if (needsCrypto.length > 0) {
    const cryptoMap = await getCryptoLogoMap(
      session.getManager(CryptoManager),
      needsCrypto.map(ticker => (ticker.symbol || ticker.name) ?? ''),
    ).catch(() => ({}));

    enriched = enriched.map(t => {
      if (t.logo || t.taxonomy !== 'tickers' || !isUSDCryptoSymbol(t.name)) return t;
      const logo = getCryptoLogo(cryptoMap, t.name ?? '');
      return logo ? { ...t, logo } : t;
    });
  }

  return enriched;
};
